import { useTranslation } from "react-i18next";
import { Card, type CardProps, Divider, Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import Button, { type ButtonProps } from "../Button";
import { FlatCard } from "../Cards/FlatCard";
import { CompletionRing } from "../CompletionRing";
import { InfoChip } from "../InfoChip";
import {
	getButtonIconAndLabel,
	getRecommendedCtaMode,
	shouldUseCompactChips,
} from "./helpers";
import type { TaskChip, TasksSectionItem } from "./types";

type Props = TasksSectionItem & {
	style?: CardProps["style"];
	/** When set, skips home CTA mode rules (mobile text / desktop by status). */
	ctaMode?: NonNullable<ButtonProps["mode"]>;
	variant?: "default" | "detailed";
};

const RING_SIZE = 48;

const TaskChipsRow = ({
	chips,
	compact,
}: {
	chips?: TaskChip[];
	compact: boolean;
}) => {
	if (!chips?.length) {
		return null;
	}
	return (
		<Box
			flexDirection="row"
			flexWrap="wrap"
			gap={4}
			flexShrink={1}
			minWidth={0}
			maxWidth="100%"
		>
			{chips.map((chip) => (
				<InfoChip
					key={chip.label}
					icon={chip.icon}
					label={compact ? (chip.compactLabel ?? chip.label) : chip.label}
				/>
			))}
		</Box>
	);
};

const DetailedFooter = ({
	body,
	ctaButton,
}: {
	body?: string;
	ctaButton: React.ReactNode;
}) => {
	const theme = useAppTheme();
	return (
		<>
			{body ? (
				<Text
					variant="bodySmall"
					style={{ color: theme.colors.onSurfaceVariant }}
				>
					{body}
				</Text>
			) : null}
			<Box gap={16}>
				<Divider />
				<Box alignItems="flex-start">{ctaButton}</Box>
			</Box>
		</>
	);
};

const DefaultFooter = ({
	hasChips,
	chipsRow,
	ctaButton,
}: {
	hasChips: boolean;
	chipsRow: React.ReactNode;
	ctaButton: React.ReactNode;
}) => (
	<Box
		width="100%"
		flexDirection="row"
		flexWrap="wrap"
		alignItems="center"
		justifyContent={hasChips ? "space-between" : "flex-end"}
		rowGap={8}
		columnGap={16}
	>
		{chipsRow}
		<Box flexShrink={0}>{ctaButton}</Box>
	</Box>
);

export const RecommendedCard = ({
	status,
	title,
	body,
	chips,
	kind,
	completion = 0,
	icon,
	ringLabel,
	ctaLabel,
	loading: isLoading = false,
	cardWidth,
	style,
	ctaMode,
	variant = "default",
	onPress,
}: Props) => {
	const { t } = useTranslation();
	const { isMobile } = useDevice();
	const buttonInfo = getButtonIconAndLabel(status, kind);
	const hasChips = Boolean(chips?.length);
	const buttonMode = ctaMode ?? getRecommendedCtaMode(status, isMobile);
	const isDetailed = variant === "detailed";

	const chipsRow = (
		<TaskChipsRow
			chips={chips}
			compact={!isDetailed && shouldUseCompactChips(cardWidth, isMobile)}
		/>
	);

	const ctaButton = (
		<Button
			disabled={isLoading}
			mode={buttonMode}
			icon={buttonInfo?.icon}
			onPress={onPress}
			loading={isLoading}
		>
			{ctaLabel ?? buttonInfo?.label ?? t("sessionConfig.start")}
		</Button>
	);

	return (
		<FlatCard
			testID="recommended-card"
			style={[{ width: cardWidth, flex: 1 }, style]}
			contentStyle={{ flexGrow: 1 }}
		>
			<Card.Content
				style={{
					flexGrow: 1,
					padding: 16,
					gap: 16,
					justifyContent: "space-between",
				}}
			>
				<Box flexDirection="row" alignItems="center" gap={8}>
					<CompletionRing
						progress={completion}
						icon={icon}
						label={isDetailed ? undefined : ringLabel}
						size={RING_SIZE}
					/>
					<Box flex={1} gap={4}>
						<Text variant="titleMedium" numberOfLines={2}>
							{title}
						</Text>
						{isDetailed ? (
							chipsRow
						) : !isMobile && body ? (
							<Text variant="bodyMedium">{body}</Text>
						) : null}
					</Box>
				</Box>
				{isDetailed ? (
					<DetailedFooter body={body} ctaButton={ctaButton} />
				) : (
					<DefaultFooter
						hasChips={hasChips}
						chipsRow={chipsRow}
						ctaButton={ctaButton}
					/>
				)}
			</Card.Content>
		</FlatCard>
	);
};
